import { creatorbase, creatorbaseQueries, Song } from '@round/api';
import { showNotification } from 'helpers';
import { Modal } from '@round/whitelabel-ui';
import styles from './DeleteSongModal.module.css';
import { Button } from '@round/whitelabel-ui';
import cn from 'classnames';
import { useDeleteSongMutation, useInvalidateMusicSong } from '@round/creatorbase-helpers/api-hooks';

const deleteUtils = creatorbaseQueries.song.utils.delete;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    song: creatorbase.Song | null;
    musicSong: Song | null;
};

const DeleteSongModal = ({ isOpen, onClose, song, musicSong }: Props) => {
    const { deleteOne, isLoading } = useDeleteSongMutation(song?.id);
    const invalidateMusicSong = useInvalidateMusicSong();

    const handleDelete = async () => {
        if (!song) {
            return;
        }

        try {
            await deleteOne(song.id);
            void invalidateMusicSong();
            showNotification('song deleted', 'info');
            onClose();
        } catch (e) {
            if (!(e instanceof Error) || !deleteUtils.isExpectedErrorResponse(e) || e.response.status !== 400) {
                showNotification('Could not delete song', 'error');
                return;
            }

            showNotification(e.response.data.join(', '), 'error');
        }
    };

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <Modal.Header>
                <Modal.Title>Delete song</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.body}>
                <p className={cn(styles.copy, styles.bold)}>You're about to permanently delete the following:</p>
                <div className={styles.banner}>
                    <p className={styles.copy}>
                        <span className={styles.bold}>Song</span>:&nbsp;
                        {song && (
                            <>
                                {musicSong?.title || song.title} by{' '}
                                {musicSong?.artists.map((a) => a.name).join(', ') || song.artist_name}
                            </>
                        )}
                    </p>
                </div>

                <p className={styles.copy}>You cannot restore songs. They are deleted permanently.</p>
            </Modal.Body>

            <Modal.Actions>
                <Button appearance="critical" isLoading={isLoading} onClick={handleDelete}>
                    Delete Song
                </Button>
                <Button appearance="ghost" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteSongModal;
